import { Row, Col, Card, CardBody, CardTitle, CardSubtitle, Table } from 'reactstrap';
import { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title, PointElement, LineElement, } from 'chart.js';
import { Pie, Bar, Line, Doughnut } from 'react-chartjs-2';
import Colors from './Colors.json'
import moment from 'moment';
import "./Dashboard.css";

ChartJS.register(ArcElement, CategoryScale, BarElement, LinearScale, PointElement, LineElement, Tooltip, Legend, Title);

function Dashboard({ filteredAgents, filteredDrivers, allLanguages, allDepartments, setActivePath, allBranches }) {
    const [showTablularView, setShowTablularView] = useState({
        weeklyStats: false,
        visitingStats: false,
        languageStats: false,
        agentStats: false,
    })
    let currentBranch = window.location.hash.replace('#', '');

    const getDriversCountByStatus = (status = [], date, filteredDrivers) => {
        let drivers = status.length > 0 ? filteredDrivers?.filter(driver => status.includes(driver.status)) : filteredDrivers
        if (date)
            drivers = drivers?.filter(driver => moment(driver.dateAdded.toDate()).format(moment.HTML5_FMT.DATE) === moment(date).format(moment.HTML5_FMT.DATE))
        return drivers?.length
    }

    const getMedian = (param1, param2, valueBy, value, filteredDrivers) => {
        let timeArray = []
        filteredDrivers?.forEach(driver => {
            if (valueBy === "day" && driver[param1] && driver[param2] && moment(driver.dateAdded.toDate()).format("dddd") === value)
                timeArray.push(moment.duration(moment(driver[param1].toDate()).diff(moment(driver[param2].toDate()))).asMinutes())
            else if (valueBy && value && driver[valueBy] === value && driver[param1] && driver[param2])
                timeArray.push(moment.duration(moment(driver[param1].toDate()).diff(moment(driver[param2].toDate()))).asMinutes())
            else if (!valueBy && !value && driver[param1] && driver[param2])
                timeArray.push(moment.duration(moment(driver[param1].toDate()).diff(moment(driver[param2].toDate()))).asMinutes())
        })
        const mid = Math.floor(timeArray.length / 2),
            nums = [...timeArray].sort((a, b) => a - b);
        return (timeArray.length % 2 !== 0 ? nums[mid] : (nums[mid - 1] + nums[mid]) / 2).toFixed(0)
    }


    const getStatsByLanguage = (language, filteredDrivers) => {
        let count = 0;
        filteredDrivers?.map(driver => {
            if (driver && driver.language && driver.language === language && driver.status === "completed")
                count++
        })
        return count;
    }

    const getDepartmentCountOnDays = (tickets, name) => {
        if (currentBranch === "FNOL")
            return moment.weekdays().map(weekDay => tickets?.filter(item => moment(item.dateAdded.toDate()).format("dddd") === weekDay && item.customBranchId === name).length)
        else
            return moment.weekdays().map(weekDay => tickets?.filter(item => moment(item.dateAdded.toDate()).format("dddd") === weekDay && item.department === name).length)
    }

    const getDepartmentCountOnAgents = (tickets, name, allAgents) => {
        if (currentBranch === "FNOL")
            return allAgents?.map(agent => tickets?.filter(item => item.agent === agent.userPk && item.customBranchId === name && item.status === "completed").length)
        else
            return allAgents?.map(agent => tickets?.filter(item => item.agent === agent.userPk && item.department === name && item.status === "completed").length)
    }


    const renderAgentStatsByDepChart = (tickets, allAgents) => {
        let delayed;
        const options = {
            responsive: true,
            animation: {
                onComplete: () => {
                    delayed = true;
                },
                delay: (context) => {
                    let delay = 0;
                    if (context.type === 'data' && context.mode === 'default' && !delayed) {
                        delay = context.dataIndex * 300 + context.datasetIndex * 100;
                    }
                    return delay;
                },
            },
            scales: {
                x: {
                    stacked: true,
                },
                y: {
                    stacked: true,
                },
            },
        };
        let agentsWithTickets = allAgents?.filter(agent => tickets.filter(ticket => ticket.agent === agent.userPk).length > 0)
        const data = {
            labels: agentsWithTickets?.map(agent => agent.username),
            datasets: currentBranch === "FNOL" ? allBranches?.map((branch, i) => ({
                label: branch.name,
                data: getDepartmentCountOnAgents(tickets, branch.customId, agentsWithTickets),
                backgroundColor: Colors[currentBranch === "FNOL" ? "Branches" : "Departments"][i]
            })) : allDepartments?.map((dept, i) => ({
                label: dept.name,
                data: getDepartmentCountOnAgents(tickets, dept.name, agentsWithTickets),
                backgroundColor: Colors[currentBranch === "FNOL" ? "Branches" : "Departments"][i]
            }))
        }
        return <Bar data={data} options={options} />
    }

    const renderStatsByWeekdaysChart = (tickets) => {
        let delayed;
        const options = {
            responsive: true,
            animation: {
                onComplete: () => {
                    delayed = true;
                },
                delay: (context) => {
                    let delay = 0;
                    if (context.type === 'data' && context.mode === 'default' && !delayed) {
                        delay = context.dataIndex * 300 + context.datasetIndex * 100;
                    }
                    return delay;
                },
            },
            scales: {
                x: {
                    stacked: true,
                },
                y: {
                    stacked: true,
                },
            },
        };
        const data = {
            labels: moment.weekdays().map(weekDay => weekDay),
            datasets: currentBranch === "FNOL" ? allBranches?.map((branch, i) => ({
                label: branch.name,
                data: getDepartmentCountOnDays(tickets, branch.customId),
                backgroundColor: Colors[currentBranch === "FNOL" ? "Branches" : "Departments"][i]
            })) : allDepartments?.map((dept, i) => ({
                label: dept.name,
                data: getDepartmentCountOnDays(tickets, dept.name),
                backgroundColor: Colors[currentBranch === "FNOL" ? "Branches" : "Departments"][i]
            }))
        }
        return <Bar data={data} options={options} />
    }

    const renderDepartmentsAndBranchChart = (filteredDrivers) => {
        const data = {
            labels: currentBranch === "FNOL" ? allBranches.map(branch => branch.name) : allDepartments.map(dep => dep.name),
            datasets: [{
                data: currentBranch === "FNOL" ? allBranches.map(branch => filteredDrivers?.filter(item => branch.customId === item.customBranchId).length) : allDepartments.map(dep => filteredDrivers?.filter(item => dep.name === item.department).length),
                backgroundColor: Colors[currentBranch === "FNOL" ? "Branches" : "Departments"],
                borderColor: Colors[currentBranch === "FNOL" ? "Branches" : "Departments"],
                borderWidth: 1,
            },
            ],
        }
        const options = {
            responsive: true,
            maintainAspectRatio: false,
        };

        return <Pie data={data} options={options} />
    }

    const renderAverageTimes = (filteredDrivers) => {
        const options = {
            responsive: true,
            aspectRatio: 1,
            animations: {
                radius: {
                    duration: 400,
                    easing: 'linear',
                    loop: (context) => context.active
                }
            },
            hoverRadius: 12,
            hoverBackgroundColor: 'red',
            interaction: {
                mode: 'nearest',
                intersect: false,
                axis: 'x'
            },
            plugins: {
                tooltip: {
                    enabled: false
                },
                legend: {
                    position: 'top',
                },
            },
        };
        const data = {
            labels: moment.weekdays().map(weekDay => weekDay),
            datasets: [["completedAt", "dateAdded"], ["completedAt", "acceptedAt"], ["acceptedAt", "dateAdded"]].map((item, i) => ({
                label: i === 0 ? 'AVG Completion Time' : i === 1 ? 'AVG Serving Time' : 'AVG Waiting Time',
                data: moment.weekdays().map(weekDay => getMedian(item[0], item[1], "day", weekDay, filteredDrivers)),
                backgroundColor: Colors["Hassan_Departments"][i],
                borderColor: Colors["Hassan_Departments"][i],
                borderWidth: 4,
            }))
        }
        return <Line options={options} data={data} />;
    }


    const renderLanguageStatsChart = (allLanguages, filteredDrivers) => {
        const data = {
            labels: allLanguages.map(lang => lang.name),
            datasets: [
                {
                    label: 'AVG Completion Time',
                    data: allLanguages.map(lang => getStatsByLanguage(lang.name, filteredDrivers)),
                    backgroundColor: Colors["Languages"],
                    borderWidth: 0,
                },
            ],
        }
        return <Doughnut data={data} />;
    }

    useEffect(() => {
        setActivePath("Dashboard")
    }, [])

    return (
        <div className="vms-dashboard-wrapper">
            <Row className="no-gutters">
                <Col>
                    <Card style={{ backgroundColor: "transparent" }}>
                        <CardBody>
                            <CardTitle className="vms-dashboard-table-title pt-0">{getDriversCountByStatus(["waiting", "assigned", "completed"], new Date(), filteredDrivers)}</CardTitle>
                            <CardSubtitle tag="h6">CHECK-IN TODAY</CardSubtitle>
                        </CardBody>
                    </Card>
                </Col>
                <Col>
                    <Card style={{ backgroundColor: "transparent" }}>
                        <CardBody>
                            <CardTitle className="vms-dashboard-table-title pt-0">{getDriversCountByStatus(["waiting"], null, filteredDrivers)}</CardTitle>
                            <CardSubtitle tag="h6">WAITING</CardSubtitle>
                        </CardBody>
                    </Card>
                </Col>
                <Col>
                    <Card style={{ backgroundColor: "transparent" }}>
                        <CardBody>
                            <CardTitle className="vms-dashboard-table-title pt-0">{getDriversCountByStatus(["assigned"], null, filteredDrivers)}</CardTitle>
                            <CardSubtitle tag="h6">SERVING</CardSubtitle>
                        </CardBody>
                    </Card>
                </Col>
                <Col>
                    <Card style={{ backgroundColor: "transparent" }}>
                        <CardBody>
                            {/* <CardTitle className="vms-dashboard-table-title pt-0">{getDriversCountByStatus(["completed"], filters.selection.endDate, filteredDrivers)}</CardTitle> */}
                            <CardTitle className="vms-dashboard-table-title pt-0">{getDriversCountByStatus(["completed"], new Date(), filteredDrivers)}</CardTitle>
                            <CardSubtitle tag="h6">SERVED TODAY</CardSubtitle>
                        </CardBody>
                    </Card>
                </Col>
                <Col>
                    <Card style={{ backgroundColor: "transparent" }}>
                        <CardBody>
                            <CardTitle className="vms-dashboard-table-title pt-0">{getDriversCountByStatus(["completed"], null, filteredDrivers)}</CardTitle>
                            <CardSubtitle tag="h6">TOTAL SERVED</CardSubtitle>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row className="no-gutters">
                <Col md="6" lg="3">
                    <Card>
                        <CardTitle className="vms-dashboard-table-title">
                            {currentBranch === "FNOL" ? "BRANCH STATS" : "REASON STATS"}
                            <i className={"m-2 float-right " + (showTablularView.visitingStats ? "fa fa-chevron-left" : "fa fa-chevron-right")} aria-hidden="true" onClick={() => setShowTablularView({ ...showTablularView, visitingStats: showTablularView.visitingStats ? false : true })} />
                        </CardTitle>
                        <CardBody className="vms-dashboard-table-wrapper">
                            {!showTablularView.visitingStats ?
                                renderDepartmentsAndBranchChart(filteredDrivers)
                                :
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th >{currentBranch === "FNOL" ? "Branch" : "Visiting Reason"}</th>
                                            <th>Total Tickets</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentBranch === "FNOL" ? allBranches.map(branch =>
                                            <tr className="text-capitalize" key={branch.customId}>
                                                <td> {branch.name} </td>
                                                <td> {filteredDrivers?.filter(item => branch.customId === item.customBranchId).length} </td>
                                            </tr>
                                        ) :
                                            allDepartments.map((dep, i) =>
                                                <tr className="text-capitalize" key={i}>
                                                    <td> {dep.name} </td>
                                                    <td> {filteredDrivers?.filter(item => dep.name === item.department).length} </td>
                                                </tr>
                                            )}
                                    </tbody>
                                </Table>
                            }
                        </CardBody>
                    </Card>
                </Col>
                <Col md="12" lg="6">
                    <Card>
                        <CardTitle className="vms-dashboard-table-title">
                            VISITING {currentBranch === "FNOL" ? "BRANCH" : "REASON"} STATS BY WEEKDAYS
                            <i className={"m-2  float-right " + (showTablularView.weeklyStats ? "fa fa-chevron-left" : "fa fa-chevron-right")} aria-hidden="true" onClick={() => setShowTablularView({ ...showTablularView, weeklyStats: showTablularView.weeklyStats ? false : true })} />
                        </CardTitle>

                        <CardBody className="vms-dashboard-table-wrapper">
                            {!showTablularView.weeklyStats ? renderStatsByWeekdaysChart(filteredDrivers) :
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th>Week Day</th>
                                            <th>Overall</th>
                                            <th>Last Week</th>
                                            <th>Current Week</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {moment.weekdays().map((weekDay, i) => <tr className="text-capitalize" key={i}>
                                            <td> <i className="fa fa-circle" aria-hidden="true" style={{ color: moment().day() === i ? "green" : "red" }} />&nbsp;&nbsp;{weekDay}</td>
                                            <td> {filteredDrivers?.filter(item => moment(item.dateAdded.toDate()).format("dddd") === weekDay).length} </td>
                                            <td> {filteredDrivers?.filter(item => moment(item.dateAdded.toDate()).format(moment.HTML5_FMT.DATE) > moment().subtract(1, 'weeks').startOf('week').format(moment.HTML5_FMT.DATE) && moment(item.dateAdded.toDate()).format(moment.HTML5_FMT.DATE) < moment().startOf("isoWeek").format(moment.HTML5_FMT.DATE) && moment(item.dateAdded.toDate()).format("dddd") === weekDay).length} </td>
                                            <td> {filteredDrivers?.filter(item => moment(item.dateAdded.toDate()).format(moment.HTML5_FMT.DATE) >= moment().startOf("isoWeek").format(moment.HTML5_FMT.DATE) && moment(item.dateAdded.toDate()).format(moment.HTML5_FMT.DATE) <= moment().format(moment.HTML5_FMT.DATE) && moment(item.dateAdded.toDate()).format("dddd") === weekDay).length} </td>
                                        </tr>)}
                                    </tbody>
                                </Table>}
                        </CardBody>
                    </Card>
                </Col>
                <Col xs="6" lg="3">
                    <Card>
                        <CardTitle className="vms-dashboard-table-title">LANGUAGE STATS
                            <i className={"m-2 float-right " + (showTablularView.languageStats ? "fa fa-chevron-left" : "fa fa-chevron-right")} aria-hidden="true" onClick={() => setShowTablularView({ ...showTablularView, languageStats: showTablularView.languageStats ? false : true })} />
                        </CardTitle>
                        <CardBody className="vms-dashboard-table-wrapper">
                            {!showTablularView.languageStats ? renderLanguageStatsChart(allLanguages, filteredDrivers) :
                                <Table responsive>
                                    <thead>
                                        <tr>
                                            <th >Language</th>
                                            <th>Total Served</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {allLanguages?.map((language) => <tr className="text-capitalize" key={language}>
                                            <td> <i className="fa fa-circle" aria-hidden="true" style={{ color: language.status === "active" ? "green" : "red" }} />&nbsp;&nbsp;{language.name}</td>
                                            <td> {getStatsByLanguage(language.name, filteredDrivers)} </td>
                                        </tr>)}
                                        {allLanguages?.length === 0 && <tr><td colSpan={11}>No Language Found!</td></tr>}
                                    </tbody>
                                </Table>
                            }
                        </CardBody>
                    </Card>
                </Col>
            </Row>
            <Row className="no-gutters">
                <Col xs="6">
                    <Card>
                        <CardTitle className="vms-dashboard-table-title">AVERAGE TIME BY WEEKDAYS</CardTitle>
                        <CardBody className="vms-dashboard-table-wrapper">
                            {renderAverageTimes(filteredDrivers)}
                        </CardBody>
                    </Card>
                </Col>
                <Col xs="6">
                    <Card>
                        <CardTitle className="vms-dashboard-table-title">
                            AGENT STATS
                            <i className={"m-2 float-right " + (showTablularView.agentStats ? "fa fa-chevron-left" : "fa fa-chevron-right")} aria-hidden="true" onClick={() => setShowTablularView({ ...showTablularView, agentStats: showTablularView.agentStats ? false : true })} />
                        </CardTitle>
                        <CardBody className="vms-dashboard-table-wrapper">
                            {!showTablularView.agentStats ?
                                renderAgentStatsByDepChart(filteredDrivers, filteredAgents)
                                :
                                <Table responsive >
                                    <thead>
                                        <tr>
                                            <th>Username</th>
                                            <th>Currently Serving</th>
                                            <th>Total Served</th>
                                            <th>Average Time</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredAgents?.map(agent => <tr className="text-capitalize" key={agent.userPk}>
                                            <td> <i className="fa fa-circle" aria-hidden="true" style={{ color: agent.status === "active" ? "green" : "red" }} />&nbsp;&nbsp;{agent.username}</td>
                                            <td>
                                                {filteredDrivers?.filter(item => item.agent === agent.userPk && item.status === "assigned")?.length > 0 ? filteredDrivers?.filter(item => item.agent === agent.userPk && item.status === "assigned")[0].name : "--"}&nbsp;:&nbsp;
                                                {filteredDrivers?.filter(item => item.agent === agent.userPk && item.status === "assigned")?.length > 0 ? moment.duration(moment().diff(moment((filteredDrivers?.filter(item => item.agent === agent.userPk && item.status === "assigned")[0].acceptedAt.toDate())))).asMinutes().toFixed(0) : "--"} Minutes ago
                                            </td>
                                            <td> {filteredDrivers?.filter(item => item.agent === agent.userPk && item.status === "completed")?.length} </td>
                                            <td> {getMedian("completedAt", "acceptedAt", "agent", agent.userPk, filteredDrivers) === "NaN" ? "--" : getMedian("completedAt", "acceptedAt", "agent", agent.userPk, filteredDrivers)} </td>
                                        </tr>)}
                                        {filteredAgents?.length === 0 && <tr><td colSpan={11}>No Agent Found!</td></tr>}
                                    </tbody>
                                </Table>
                            }
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    )
}

export default Dashboard

